import { useEffect, useState } from "react";
import moment from "moment";

import { Button, Dialog, FilePicker } from "@rio-cloud/rio-uikit";
import ErrorDialog from "../../../components/dialogs/ErrorDialog";
import ConfirmDialog from "../../../components/dialogs/ConfirmDialog";
import { formatFileSize } from "../../../utils/imageSizeUtils";
import { fetchFilesSupport, FileAttachment, uploadFileSupport, removeFile, downloadFile } from "../../../api/tas";
import { useSelector } from "react-redux";
import { getUserProfile } from "../../../configuration/login/loginSlice";
import { suspendPageLoad } from "../../../routes/suspendPageLoad";

const CarouselPreview = suspendPageLoad(() => import("./CarouselPreview"));

interface IUploadProps {
  id: string;
  path: string;
  disabled: boolean;
  showList: boolean;
  isControlTower: boolean;
}

const getFiles = async (path: string) => {
  const response = await fetchFilesSupport(path);
  return response.content;
};

const handleDownload = async (path: string, fileId: string, fileName: string) => {
  const blob = await downloadFile(path, fileId);

  if (!blob) return;

  const url = window.URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;

  document.body.appendChild(a);
  a.click();
  a.remove();

  window.URL.revokeObjectURL(url);
};

const Upload = (props: IUploadProps) => {
  const [files, setFiles] = useState<FileAttachment[]>([]);
  const [previewFileIndex, setPreviewFileIndex] = useState<number | null>(null);
  const [uploadErrorMessage, setUploadErrorMessage] = useState<
    string | undefined
  >(undefined);

  const fetchData = async () => {
    const files = await getFiles(props.path);
    setFiles(files);
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <div id={props.id}>

      <div className="padding-15 border rounded">
        <div className="margin-bottom-20">
          <span className="text-medium">Novo arquivo</span>
        </div>
        {!props.disabled && (
          <FilePicker
            displayMode="dropzone"
            className=""
            onPick={async (acceptedFiles: FileList | null) => {
              const maxSize = 20 * 1024 * 1024;
              const filePicked = acceptedFiles?.[0];

              if (!filePicked) return;

              if (filePicked.size > maxSize) {
                return setUploadErrorMessage(
                  "O arquivo selecionado excede o limite de 20MB. Escolha um arquivo menor."
                );
              }

              if (acceptedFiles?.[0]) {
                const uploaded = await uploadFileSupport({
                  path: props.path,
                  file: Array.from(acceptedFiles),
                });
                if (!uploaded)
                  return setUploadErrorMessage(
                    "Arquivo invalido. Erro ao realizar o upload, verifique o arquivo e tente novamente."
                  );
                fetchData();
              }
            }}
            multiple={true}
          //   accept={accept}
          >
            {dropArea}
          </FilePicker>
        )}
      </div>

      {props.showList ? (
        <div className="width-100pct border rounded padding-15 margin-top-15">
          <div className="margin-bottom-20">
            <span className="text-medium">Arquivos enviados</span>
          </div>

          {files?.length > 0 ? (
            files.map((file, index) => (
              <FilePreview
                key={index}
                file={file}
                path={props.path}
                onPreview={() => setPreviewFileIndex(index)}
                remove={() => {
                  setFiles((prevFiles) =>
                    (prevFiles ?? []).filter((f) => f.fileId !== file.fileId)
                  );
                  setPreviewFileIndex((currentIndex) => {
                    if (currentIndex === null) return null;
                    if (currentIndex === index) return null;
                    if (currentIndex > index) return currentIndex - 1;
                    return currentIndex;
                  });
                }}
                disabled={!props.isControlTower}
              />
            ))
          ) : (
            <div className="text-color-dark text-italic">Nenhum arquivo</div>
          )}
        </div>
      ) : null}

      <Dialog
        show={previewFileIndex !== null}
        title={
          previewFileIndex !== null ? files[previewFileIndex]?.name : undefined
        }
        onEsc={() => setPreviewFileIndex(null)}
        onClose={() => setPreviewFileIndex(null)}
        bsSize="lg"
        body={
          previewFileIndex !== null ? (
            <CarouselPreview
              files={files}
              selectedIndex={previewFileIndex}
              onSelectIndex={setPreviewFileIndex}
              path={props.path}
            />
          ) : null
        }
        footer={
          <>
            <div className="display-flex justify-content-end">
              {previewFileIndex !== null && files[previewFileIndex]?.url && (
                <Button
                  bsStyle="primary"
                  className="margin-right-10"
                  iconName="rioglyph rioglyph-download"
                  onClick={() => {
                    const file = files[previewFileIndex];
                    if (file) {
                      handleDownload(props.path, file.fileId, file.name);
                    }
                  }}
                >
                  Download
                </Button>
              )}
              <Button bsStyle="default" onClick={() => setPreviewFileIndex(null)}>
                Fechar
              </Button>
            </div>
          </>
        }
      />

      <ErrorDialog
        show={!!uploadErrorMessage}
        title={"Erro ao fazer upload"}
        body={uploadErrorMessage ?? ""}
        onClose={() => setUploadErrorMessage(undefined)}
      />
    </div>
  );
};

const dropArea = ({ isDragActive }: { isDragActive: boolean }) => (
  <div className={`rounded border border-style-dashed border-color-gray bg-lightest text-size-14 text-center text-color-darker opacity-50 padding-25 cursor-pointer`}>
    <span className="rioglyph rioglyph-files text-size-200pct opacity-50 margin-top-20" />
    <div className="margin-top-10">
      {isDragActive
        ? "Jogue os arquivos aqui ..."
        : "Arraste seus arquivos para cá ou clique para selecionar no computador"}
    </div>
  </div>
);

const FilePreview = ({
  file,
  path,
  onPreview,
  remove,
  disabled,
}: {
  file: FileAttachment | undefined;
  path: string;
  onPreview: () => void;
  remove: () => void;
  disabled: boolean;
}) => {
  const [deleteFileDialog, setDeleteFileDialog] = useState<boolean>(false);
  const userProfile = useSelector(getUserProfile);
  const userId = userProfile?.sub?.split(":")?.[1];

  return (
    <>
      <div className="margin-bottom-10">
        <span className="text-size-14 text-color-gray">
          {file?.uploadedBy} - {file?.uploadedAt
            ? moment(file.uploadedAt).format("DD/MM/YYYY HH:mm")
            : "Data não disponível"}
        </span>
      </div>

      <div
        className="panel panel-default cursor-pointer"
        onClick={() => {
          if (file) onPreview();
        }}
      >
        <div className="panel-body">
          {file && (
            <div className="flex">
              <div className="flex justify-between w-full">
                <div>
                  <div>
                    <b>{`${file?.name}`}</b>
                  </div>
                  <ul className='margin-0'>
                    <li>
                      <label htmlFor="fileType" className='margin-right-10'>type: </label>
                      <i id="fileType">{`${file?.fileType}`}</i>
                    </li>
                    <li>
                      <label htmlFor="fileSize" className='margin-right-10'>size: </label>
                      <i id="fileSize">{`${formatFileSize(file?.fileSize)}`}</i>
                    </li>
                  </ul>
                </div>

                {!disabled && (
                  <Button
                    bsStyle={Button.MUTED}
                    iconOnly
                    disabled={userId !== file.uploadedById}
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteFileDialog(true);
                    }}>
                    <span className='rioglyph rioglyph-remove' />
                  </Button>
                )}
              </div>
            </div>
          )}
          {!file && (
            <div className="text-color-dark text-italic">Nenhum arquivo</div>
          )}
        </div>
      </div>

      <ConfirmDialog
        show={deleteFileDialog}
        title="Remover arquivo"
        body="Deseja realmente remover o arquivo?"
        onClose={() => setDeleteFileDialog(false)}
        onConfirm={async () => {
          if (file) {
            const response = await removeFile(path, file.fileId);
            if (response) {
              remove();
              setDeleteFileDialog(false);
            }
          }
        }}
      />
    </>
  );
};

export default Upload;
